// Forms — shared safe Zod resolver (use instead of @hookform/resolvers/zod)
export { safeZodResolver, zodResolver } from "./forms/safe-zod-resolver.js";
export { scrollToFirstError } from "./forms/scroll-to-first-error.js";

// Components
export { Button } from "./components/button.js";
export { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/card.js";
export { SimpleVideoPlayer } from "./components/simple-video-player.js";
export { Testimonials } from "./components/testimonials.js";
export type { Testimonial, VideoTestimonial } from "./components/testimonials.js";
export { PartnerSlackCTA } from "./components/PartnerSlackCTA.js";
export type { PartnerSlackCTAProps } from "./components/PartnerSlackCTA.js";

// Booking Components
export * from "./components/booking/index.js";
// Needs the hooks/api calendar-invite handler chain (@west-tennessee-consulting/email), not in this
// repo (private/out-of-scope in wtc-monorepo).
export { PublicBookingStepper } from "./components/unifiedtime/PublicBookingStepper.js";
export { BookingLinkBuilderPanel } from "./components/unifiedtime/BookingLinkBuilderPanel.js";

// Booking Hooks
export * from "./hooks/booking/index.js";

// Shared Components
export { TimeSlotGrid } from "./components/shared/TimeSlotGrid.js";
export { AdvancedBookingForm } from "./components/shared/AdvancedBookingForm.js";
export { default as BookingModal } from "./components/shared/BookingModal.js";

// UI Components
export { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/tabs.js";
export { EnhancedTabs, EnhancedTabsList, EnhancedTabsTrigger, EnhancedTabsContent } from "./components/enhanced-tabs.js";
export { Badge, badgeVariants } from "./components/badge.js";

// Safe Components (Context-independent alternatives)
export { SafeCloseButton, useSafeClose, withSafeClose } from "./components/safe-close-button.js";
export { Alert, AlertTitle, AlertDescription } from "./components/alert.js";

// Mantine Integration
export { MantineProvider } from "./components/mantine-provider.js";
export { MantineModalStack, useUrlModalStack } from "./components/mantine-modal-stack.js";
export { MantineDrawerStack, useUrlDrawerStack } from "./components/mantine-drawer-stack.js";
export { SimpleModal } from "./components/simple-modal.js";
export { SimpleDrawer } from "./components/simple-drawer.js";
export { GlobalModalStackProvider, useGlobalModalStack, GlobalModalStackContext } from "./components/global-modal-stack/index.js";
export { MODAL_IDS, GLOBAL_MODAL_IDS } from "./components/global-modal-stack/constants.js";
export type { ModalId } from "./components/global-modal-stack/constants.js";
export { ModalContainer } from "./components/global-modal-stack/ModalContainer.js";
export type { ModalContainerProps } from "./components/global-modal-stack/ModalContainer.js";
export { GlobalDrawerStackProvider, useGlobalDrawerStack, GlobalDrawerStackContext, CentralizedDrawer } from "./components/global-drawer-stack.js";
export { useStackModal } from "./hooks/use-stack-modal.js";
export { useStackDrawer } from "./hooks/use-stack-drawer.js";
export { MantineSelect, MantineMultiSelect } from "./components/mantine-select.js";
export { 
  Dialog, 
  DialogPortal, 
  DialogOverlay, 
  DialogClose, 
  DialogTrigger, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription 
} from "./components/dialog.js";
export { ConfirmationModal } from "./components/confirmation-modal.js";
export { ResponsiveModal, useResponsiveModal } from "./components/responsive-modal.js";
export type { ResponsiveModalProps } from "./components/responsive-modal.js";
// export { MantineModalStack, useUrlModalStack } from "./components/mantine-modal-stack.js";

// Drawer Stack System
export { 
  DrawerStackProvider, 
  useDrawerStack, 
  useDrawerStackRequired,
  useDrawerLevel, 
  useDrawerZIndex, 
  useCanNest, 
  useShouldReplace 
} from "./lib/contexts/drawer-stack-context.js";
export { 
  drawerRouter, 
  createDrawerContext, 
  analyzeContentComplexity, 
  detectContentType 
} from "./lib/drawer-router.js";
export type { 
  DrawerItem, 
  DrawerConfig, 
  DrawerStackContextType,
  DrawerContext,
  DrawerRoutingRules
} from "./lib/contexts/drawer-stack-context.js";
export { Input } from "./components/input.js";
export { Label } from "./components/label.js";
export { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./components/select.js";
export { Textarea } from "./components/textarea.js";
export { LoadingSpinner } from "./components/loading-spinner.js";
export { Skeleton } from "./components/skeleton.js";
export { DiagnosticButton } from "./components/DiagnosticButton.js";
export { AdvancedList } from "./components/advanced-list/AdvancedList.js";
export type { AdvancedListProps } from "./components/advanced-list/AdvancedList.js";
export { useAdvancedList } from "./components/advanced-list/useAdvancedList.js";
export type {
  UseAdvancedListOptions,
  UseAdvancedListResult,
} from "./components/advanced-list/useAdvancedList.js";
export {
  applyAdvancedListQuery,
  groupAdvancedListItems,
  defaultAdvancedListQueryState,
} from "./components/advanced-list/advanced-list-query.js";
export type {
  AdvancedListFilterDef,
  AdvancedListSortDef,
  AdvancedListQueryState,
} from "./components/advanced-list/advanced-list-query.js";
export { Accordion, AccordionContent, AccordionItem, AccordionItemWithContext, AccordionTrigger } from "./components/accordion.js";
export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/tooltip.js";
export { HoverTip } from "./components/hover-tip.js";
export type { HoverTipProps } from "./components/hover-tip.js";
export { Separator } from "./components/separator.js";
export { ScrollArea, ScrollBar } from "./components/scroll-area.js";

// Table Components
export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from "./components/table.js";

// Drawer Components
export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from "./components/drawer.js";

// Form Components (react-hook-form integration)
export {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  useFormField,
} from "./components/form.js";

// Checkbox Component
export { Checkbox } from "./components/checkbox.js";

// Switch Component
export { Switch } from "./components/switch.js";

// Calendar Component
export { Calendar } from "./components/calendar.js";

// Markdown Components
export { MarkdownRenderer } from "./components/markdown/markdown-renderer.js";

// Hooks
export { useCopyToClipboard } from "./hooks/useCopyToClipboard.js";
export { useIsMobile } from "./hooks/useIsMobile.js";
export { useGoogleOAuth } from "./hooks/use-google-oauth.js";

// Search Components
export { AdvancedFullPageSearch } from "./components/AdvancedFullPageSearch.js";
export { SearchFilters } from "./components/SearchFilters.js";
export { SearchResultGroup } from "./components/SearchResultGroup.js";
export { SearchResultGroupCollapsible } from "./components/SearchResultGroupCollapsible.js";
export { SearchResultGroupSidebar } from "./components/SearchResultGroupSidebar.js";
export { SearchSuggestions } from "./components/SearchSuggestions.js";
export { SearchLoadingSkeleton } from "./components/SearchLoadingSkeleton.js";
export { SearchHistoryDrawer } from "./components/SearchHistoryDrawer.js";
export { SearchModal } from "./components/SearchModal.js";
export { SearchFilterChips } from "./components/SearchFilterChips.js";
export { SearchFilterPopover } from "./components/SearchFilterPopover.js";
export type { SearchResult, UseAlgoliaSearchReturn } from "./components/AdvancedFullPageSearch.js";

// Search Hooks
export { useSearchHistory } from "./hooks/useSearchHistory.js";
export type { SearchHistoryItem } from "./hooks/useSearchHistory.js";
export { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./components/alert-dialog.js";
export { Popover, PopoverContent, PopoverTrigger } from "./components/popover.js";
export { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuCheckboxItem, 
  DropdownMenuRadioGroup, 
  DropdownMenuRadioItem, 
  DropdownMenuSeparator, 
  DropdownMenuShortcut, 
  DropdownMenuSub, 
  DropdownMenuSubTrigger, 
  DropdownMenuSubContent 
} from "./components/dropdown-menu.js";
export { RadioGroup, RadioGroupItem } from "./components/radio-group.js";
export { Toaster, showToast } from "./components/toast.js";

// Responsive Select Components
export { ResponsiveCommandSelect, ResponsivePopover } from "./components/responsive-select/index.js";
export type { SelectOption, BaseSelectProps, RecentItem } from "./components/responsive-select/index.js";

// OAuth Components
export { GoogleOAuthSetup } from "./components/unifiedtime/google/GoogleOAuthSetup.js";
export { MicrosoftOAuthSetup } from "./components/unifiedtime/microsoft/MicrosoftOAuthSetup.js";
export { default as OAuthIntermediatePage } from "./components/oauth/OAuthIntermediatePage.js";
export { GoogleSignInButton } from "./components/oauth/GoogleSignInButton.js";

// Shared Auth Components
export { SharedAuthPage, SharedAuthProvider, useSharedAuth, getAuthConfig, defaultAuthConfig, appConfigs } from "./components/auth/index.js";
export type { AuthConfig } from "./components/auth/index.js";

export { default as SetupPage } from "./components/unifiedtime/setup-page.js";

export { default as CalendarHomePage } from "./components/unifiedtime/calendar-home-page.js";
export { default as BookPage } from "./components/unifiedtime/book-page.js";
export { default as GooglePage } from "./components/unifiedtime/google-page.js";
export { default as MicrosoftPage } from "./components/unifiedtime/microsoft-page.js";
export { default as UnifiedPage } from "./components/unifiedtime/unified-page.js";
export { default as PublicBookingPage } from "./components/unifiedtime/public-booking-page.js";

export { AdvancedCalendar } from "./components/unifiedtime/AdvancedCalendar.js";
export { EventDetailsModal } from "./components/unifiedtime/EventDetailsModal.js";
export { EventEditDialog } from "./components/unifiedtime/EventEditDialog.js";
export { EventAttendeesTab } from "./components/unifiedtime/EventAttendeesTab.js";
export { AttendeeDetailsModal } from "./components/unifiedtime/AttendeeDetailsModal.js";
export { PersonSelector } from "./components/unifiedtime/PersonSelector.js";
export { CalendarMultiSelector } from "./components/unifiedtime/CalendarMultiSelector.js";
export { CreateMeetingModal } from "./components/unifiedtime/CreateMeetingModal.js";

// Attendee Components
export { AttendeeAvatar } from "./components/unifiedtime/attendee/AttendeeAvatar.js";
export { AttendeeStatusBadge } from "./components/unifiedtime/attendee/AttendeeStatusBadge.js";
export { AttendeeItem } from "./components/unifiedtime/attendee/AttendeeItem.js";
export { AttendeeList } from "./components/unifiedtime/attendee/AttendeeList.js";
export { default as EnhancedAttendeeFields } from "./components/unifiedtime/attendees/EnhancedAttendeeFields.js";
export { AttendeeChip } from "./components/unifiedtime/attendees/AttendeeChip.js";
export { AttendeeChips } from "./components/unifiedtime/attendees/AttendeeChips.js";
export { AttendeeSelectWithModal } from "./components/unifiedtime/attendees/AttendeeSelectWithModal.js";
export { default as CreateEventModal } from "./components/unifiedtime/calendarinvites/microsoft/CreateEventModal.js";

// Shared UI Components
export { SectionHeader } from "./components/unifiedtime/shared/SectionHeader.js";
export { EmptyState } from "./components/unifiedtime/shared/EmptyState.js";
export { LocalizedLink } from "./components/shared/LocalizedLink.js";
export { ActionButtonGroup } from "./components/unifiedtime/shared/ActionButtonGroup.js";
export { StandardModal } from "./components/unifiedtime/shared/StandardModal.js";
export { SummaryStats } from "./components/unifiedtime/shared/SummaryStats.js";

// Hooks
export { useToast } from "./hooks/use-toast.js";
export { useLocalDrawerSearch, useAsyncDrawerSearch, useDrawerSearch } from "./hooks/use-drawer-search.js";
export { useAttendeeData } from "./hooks/useAttendeeData.js";
export { useAuth } from "./hooks/useAuth.js";
export { useUrlModalControls } from "./hooks/use-url-modal-controls.js";
export { useUrlModal } from "./hooks/use-url-modal.js";
export {
  useHistoryModal,
  type UseHistoryModalOptions,
  type UseHistoryModalReturn,
} from "./hooks/use-history-modal.js";
export {
  useAccountStatus,
  useRemoveConnectionMutation,
  useGoogleCalendarData,
  useMicrosoftCalendarData,
  useCombinedCalendarData,
  useOpenSlots,
  useBookingMutation,
  useResetAccountMutation,
  useGoogleOAuthSetup,
  useMicrosoftOAuthSetup,
  useCRMSearch,
  useExternalGoogleEvents,
  useExternalMicrosoftEvents,
  useCreateGoogleEvent,
  useCreateMicrosoftEvent,
  useDeleteGoogleEvent,
  useDeleteMicrosoftEvent,
  useDeleteEvent,
  useUpdateGoogleEvent,
  useUpdateMicrosoftEvent,
  useUpdateEvent
} from "./hooks/use-api.js";
export { useAccountRequired } from "./hooks/useAccountRequired.js";
export { useBookingMutation as useBookingMutationFromFile } from "./hooks/useBookingMutation.js";

// Utils
export { cn } from "./lib/utils.js";
export { generateGoogleAuthUrl, generateMicrosoftAuthUrl } from "./lib/oauth-utils.js";
export { apiGet, apiPost, apiDelete, apiPut } from "./lib/api-utils.js";
export { joinPartnerEcosystem } from "./lib/partner-ecosystem.js";
export type { JoinPartnerEcosystemOptions, JoinPartnerEcosystemResponse } from "./lib/partner-ecosystem.js";

// Attendee Utils
export {
  normalizeGoogleAttendees,
  normalizeMicrosoftAttendees,
  normalizeGoogleOrganizer,
  normalizeMicrosoftOrganizer,
  normalizeStringAttendees,
  normalizeStringOrganizer,
  extractAttendeeEmails,
  groupAttendeesByStatus,
  type AttendeeData
} from "./lib/attendee-utils.js";

// Auth Utils — NOT re-exported from the barrel because `./lib/auth-utils`
// transitively imports `next/server`, which is forbidden in the Chrome
// extension bundle. Import directly from `@west-tennessee-consulting/ui/lib/auth-utils` in
// server-only code (Hub API routes, unifiedtime API routes).

// Universal Auth Check (Chrome redirect fix)
export {
  hasAuthCookies,
  getCachedAuthState,
  setCachedAuthState,
  clearCachedAuthState,
  isLikelyAuthenticated,
  checkAuthWithServer,
  checkAuthentication,
  useUniversalAuthCheck,
  detectAuthToken,
  debugAuthSources
} from "./lib/auth-check.js";
export type { AuthTokenInfo } from "./lib/auth-check.js";

// Optimal Auth System (Best Practices Implementation)
export {
  OptimalAuthProvider,
  useOptimalAuth,
  UniversalAuthProvider, // Drop-in replacement
  useUniversalAuth,      // Drop-in replacement
  useUniversalAuthOptional, // Non-throwing variant for not-found / error boundaries
  SimpleAuthProvider,
  useSimpleAuth,
  useSimpleAuthOptional,
  UserDataProvider,
  useUserData,
  useUserDataOptional
} from "./lib/auth/OptimalAuthProvider.js";

// Supabase Utils
export {
  getGoogleTokensFromSupabase,
  saveGoogleTokensToSupabase,
  getMsftTokensFromSupabase,
  saveMsftTokensToSupabase,
  deleteGoogleTokensFromSupabase,
  deleteMsftTokensFromSupabase,
  logAuditEvent
} from "./lib/supabase.js";

// Debug Logger
export { DebugLogger, startTimer } from "./lib/debug-logger.js";

// Calendar Service
export {
  CalendarService,
  DEFAULT_CONFIG,
  CalendarAvailabilityError,
  CALENDAR_DEFAULT_TIMEZONE,
  normalizeSlotConfig,
  holidayKeysForYear,
} from "./lib/calendar-service.js";
export type {
  TimeSlot as CalendarTimeSlot,
  BusyEventLike as CalendarBusyEventLike,
  BusyInterval as CalendarBusyInterval,
  CalendarEventLike,
  CalendarEventsResult,
  RawEventTime as CalendarRawEventTime,
  SlotConfig as CalendarSlotConfig,
  SlotConfigInput as CalendarSlotConfigInput,
} from "./lib/calendar-service.js";

export {
  createCalendarEvent,
  getCalendarCreationHeaders,
  createErrorResponse as createCalendarErrorResponse,
  validateCalendarCreationRequest,
  getCalendarTokens,
  transformToGoogleEventFormat,
  transformToMicrosoftEventFormat,
  createGoogleCalendarEvent,
  createMicrosoftCalendarEvent,
  type CalendarEventRequest,
  type CalendarEventResponse,
  type CalendarCreationResult,
  type CalendarProvider
} from "./lib/calendar-creation-service.js";

// API Shared Utils
export {
  getGoogleUserEmail,
  getMicrosoftUserEmail,
  getGoogleCredentials,
  getMicrosoftCredentials,
  // USER_OAUTH_TOKEN_REFRESH: `refreshGoogleAccessToken` was removed here in
  // Phase 4.5 wave 5, once its last consumer (an apps/unifiedtime calendar route)
  // adopted the shared seam over the ONE `@west-tennessee-consulting/oauth-connections` engine. Its
  // Microsoft twin and the `refreshAccessToken` / `getUserEmail*` wrappers went in
  // wave 4. Do not re-export a refresh from this browser-reachable barrel.
  fetchGoogleCalendarEvents,
  fetchMicrosoftCalendarEvents,
  fetchExternalGoogleCalendarEvents,
  fetchExternalMicrosoftCalendarEvents,
  fetchCalendarEvents,
  fetchExternalCalendarEvents,
  isValidEmail,
  sanitizeInput,
  createErrorResponse,
  createSuccessResponse,
  handleCorsPreflight,
  createCorsHeaders,
  createMethodNotAllowedResponse,
  createAuthRequiredResponse,
  createMissingParamsResponse,
  type AccountStatus
} from "./lib/api-shared-utils.js";

// API Route Handlers
// Needs @west-tennessee-consulting/supabase (getServiceSupabaseOrThrow, dynamic import) and
// ./handlers/public-booking-calendar-invite.js (@west-tennessee-consulting/email), neither in this
// repo (private/out-of-scope in wtc-monorepo).
export {
  handleGetAccountStatus,
  handleGetOpenSlots,
  handlePublicBooking,
  handleGetPublicAvailability,
  handleClearAccount,
  handleRemoveConnection,
  handleExternalEventBooking,
  handleGetGoogleCalendarEvents,
  handleGetMicrosoftCalendarEvents,
  createRouteContext,
  createPublicRouteContext,
  handleRouteError,
  type RouteContext,
  type OpenSlotsParams,
  type PublicBookingParams
} from "./lib/api-route-handlers.js";

// Booking Configuration
export {
  getEventTypeConfig,
  getAllEventTypes,
  validateEventTypeConfig,
  type EventTypeConfig
} from "./lib/booking/event-types-config.js";

export {
  getNextAssignee,
  updateRoundRobinState
} from "./lib/booking/round-robin-assignment.js";

// Date utilities
export {
  parseDateString,
  formatDateString,
  isWeekend,
  isWeekday,
  getDayName,
  addDays,
  startOfDay,
  endOfDay,
  isSameDay,
  createDateRange,
  parseAndValidateDate
} from "./lib/date-utils.js";

// Route Utilities
export {
  createRouteHandler,
  createGetRoute,
  createPostRoute,
  createPutRoute,
  createDeleteRoute,
  createMethodNotAllowedHandlers,
  handleCorsPreflightRequest,
  parseRequestBody,
  validateRequestData,
  sanitizeInputs,
  CommonValidationRules,
  createSuccessResponseWithData,
  createStandardErrorResponse,
  createValidationErrorResponse,
  type RouteConfig,
  type ValidationRule,
  type RouteHandlerOptions
} from "./lib/route-utilities.js";

// CSV Export
export { downloadCSV, formatArrayForCSV, formatBooleanForCSV, formatCurrencyForCSV, formatDateForCSV, formatNumberForCSV, objectsToCSV, sanitizeFieldName } from "./lib/csv-export.js";

// Currency / BigFloat display (canonical — BigFloat outputs are strings)
export { formatCurrency, formatNumber, formatWholeCurrency, parseBigFloat } from "./lib/currency.js";
export type { BigFloatValue, FormatCurrencyOptions, FormatNumberOptions } from "./lib/currency.js";

// User Types and Constants
export * from "./lib/constants/user-types.js";

export { createClient } from "./lib/supabase/client.js";
export { setGlobalAuthState, allowAuthContextCall, resetAuthContextCallTracking } from "./lib/supabase/global-auth-guard.js";

export {
  signOut,
  signOutEverywhere,
  signOutOtherDevices,
} from "./lib/utils/sign-out.js";
export { useSignOut } from "./hooks/use-sign-out.js";
export type { SignOutOptions, SignOutResult } from "./lib/utils/sign-out.js";

export {
  signInWithSupabase,
  signUpWithSupabase,
  signOutWithSupabase,
  getCurrentSupabaseUser,
  createSupabaseUser,
  updateSupabaseUserMetadata,
  deleteSupabaseUser
} from "./lib/supabase-auth.js";

// Supabase Auth Context

// Legacy Universal Auth Context (deprecated - use OptimalAuthProvider instead)
// export { UniversalAuthProvider, useUniversalAuth } from "./lib/universal-auth-context.js";

export { SupabaseProtectedRoute, SupabasePublicRoute } from "./lib/contexts/supabase-auth-context.js";

export { getBrowserSupabaseClient } from "./lib/supabase-browser.js";

// HTTP client and authed query helper
export { apiClient, setAuthTokenGetter } from "./lib/http/client.js";
export { useAuthedQuery } from "./lib/query/useAuthedQuery.js";

// Preferences Context
export { PreferencesProvider, usePreferences } from "./lib/preferences-context.js";

export {
  CalendarProvider as CalendarContextProvider,
  useCalendarContext,
  useCalendarEvents,
  useCalendarEvent,
  useCalendarSources,
  useSourceColors,
  useSourceColor,
  type CalendarViewType
} from "./lib/calendar-context.js";

// FullCalendar shared CSS
// Note: CSS files are imported by path; no TS type is needed

// Affiliate tracking lived here as a 442-line SECOND copy of
// `@west-tennessee-consulting/tracking/src/affiliate-tracker.ts`. It is gone: one tracker now, in
// `@west-tennessee-consulting/tracking`, which is a leaf package with no React/UI dependencies. Had
// the survivor been this copy instead, `@west-tennessee-consulting/analytics` — which the unified
// tracker reads the affiliate ref from — would have joined the dependency graph
// of every app that imports a button from this barrel.
//
// Import `AffiliateTracking`, `trackAffiliateLanding`, `getSessionId` and
// friends from `@west-tennessee-consulting/tracking` instead.

// Hub unified calendar agenda (fetch-based; safe for non-Next contexts)
export {
  fetchHubUnifiedCalendarAgenda,
  type FetchHubUnifiedCalendarAgendaOptions,
  type HubUnifiedCalendarAgendaResult,
} from "./lib/hub-agenda.js";
export { WTC_EMAIL_CALENDAR_SOURCE_ID, formatWtcEmailCalendarSourceForDisplay } from "./hooks/api/calendar/constants.js";

// Types
export * from "./types/booking.js";
export * from "./types/calendar.js";
export * from "./lib/unified-calendar-shared-team-calendars.js";
export * from "./lib/types/user.js";
export * from "./lib/types/auth.js";
export * from "./lib/constants/testimonials.js";

export { OrganizationSwitcher } from "./components/organization/OrganizationSwitcher.js";
export { TeamMembersList } from "./components/organization/TeamMembersList.js";
export { InviteMemberModal } from "./components/organization/InviteMemberModal.js";

// Organization Provider
export { OrganizationProvider, useOrganization } from "./lib/organization/OrganizationProvider.js";


// Charts — one dependency-free SVG chart engine shared by the website blog
// (```chart MDX blocks) and Meridian `chart` artifacts. See
// ./components/charts/ChartRenderer.
export { ChartSpecSchema, renderChart, type ChartSpec } from "./components/charts/ChartRenderer.js";
